<template>
  <div class="games-view">
    <!-- Dateline / broadsheet header -->
    <div class="games-view__dateline">
      <div class="games-view__dateline-inner">
        <span class="dateline__item">MLB &middot; The Slate</span>
        <span class="dateline__item dateline__item--center">
          Tonight's Slate
          <span class="dateline__dot">♦</span>
          N° {{ slateNumber }}
        </span>
        <span class="dateline__item dateline__item--right">{{ formattedDate }}</span>
      </div>
    </div>

    <!-- Section header -->
    <section class="games-view__header" aria-label="Upcoming Games header">
      <div class="games-view__header-inner">
        <div class="section__header">
          <div class="section-number">§</div>
          <div class="section__titles">
            <p class="eyebrow">Upcoming Games &mdash; Schedule, Odds, Edges</p>
            <h1 class="display-lg">What's on the card <em>today.</em></h1>
            <p class="body-lg section__dek">
              Every matchup, every line, every simulated edge. Filed nightly from the pressbox,
              updated as lineups are posted.
            </p>
          </div>
        </div>
      </div>
    </section>

    <hr class="rule rule--ox games-view__rule" />

    <!-- Panel -->
    <section class="games-view__panel" aria-label="Upcoming Games">
      <div class="games-view__panel-inner">
        <TomorrowsGamesPanel />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import TomorrowsGamesPanel from '@/components/TomorrowsGamesPanel.vue'

const now = new Date()

const formattedDate = computed((): string =>
  now
    .toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    .toUpperCase()
)

const slateNumber = computed((): string => {
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000
  )
  return String(dayOfYear)
})
</script>

<style scoped>
.games-view {
  padding-top: 4.75rem;
  color: var(--ink);
  min-height: 100vh;
}

/* ─── Dateline ─────────────────────────────────────────── */
.games-view__dateline {
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  background: var(--paper);
}

.games-view__dateline-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.55rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1.5rem;
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-medium);
}

.dateline__item--center {
  text-align: center;
}

.dateline__item--right {
  text-align: right;
}

.dateline__dot {
  display: inline-block;
  margin: 0 0.6em;
  color: var(--ox);
  transform: translateY(-1px);
}

/* ─── Section header ───────────────────────────────────── */
.games-view__header {
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 2rem 1.5rem;
}

.games-view__header-inner {
  max-width: 1280px;
}

.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.section__titles .eyebrow {
  margin-bottom: 1rem;
}

.section__titles .display-lg em {
  font-style: italic;
  color: var(--ox);
}

.section__dek {
  margin-top: 1.25rem;
  max-width: 58ch;
}

.games-view__rule {
  max-width: 1280px;
  margin: 1.5rem auto 0;
}

/* ─── Panel ───────────────────────────────────────────── */
.games-view__panel {
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.5rem 2rem 5rem;
}

.games-view__panel-inner {
  width: 100%;
}

/* ─── Responsive ──────────────────────────────────────── */
@media (max-width: 700px) {
  .games-view__dateline-inner {
    grid-template-columns: 1fr;
    gap: 0.35rem;
    text-align: center;
    padding: 0.55rem 1rem;
  }

  .dateline__item--right {
    text-align: center;
  }

  .games-view__header {
    padding: 2rem 1rem 1rem;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .games-view__panel {
    padding: 1.5rem 1rem 3rem;
  }
}
</style>
